import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  getSysPrompts, 
  getSysPrompt, 
  createSysPrompt, 
  updateSysPrompt, 
  deleteSysPrompt,
  // Inline Prompt APIs
  getInlinePrompts,
  getInlinePromptDetail,
  updateInlinePrompt,
  createInlinePrompt,
  deleteInlinePrompt,
  batchUploadInlinePrompts,
  type BatchUploadResult,
  // Curriculum Pipeline
  runCurriculumPipeline,
  type CurriculumPipelineProgressMessage,
  PIPELINE_OPTIONS,
  // Slides Pipeline
  runSlidesPipeline,
  type SlidesPipelineProgressMessage,
  SLIDES_PIPELINE_OPTIONS,
  // OSS Upload
  uploadToOSS
} from '../lib/api';
import { 
  Save, Plus, Trash2, FileText, Layout, ChevronRight, Check, X, Filter, ArrowLeft, 
  Loader2, ShieldAlert, Lock, ChevronDown, Layers, Download, FolderOpen, Upload, AlertCircle,
  Sparkles, Paperclip
} from 'lucide-react'; 
import { useAuth } from '../contexts/AuthContext'; 

// --- Types ---
type Prompt = {
  id: number;
  name: string;
  category: string;
  prompt_content?: string; 
};

type InlinePrompt = {
  id: number;
  sys_prompt_id: number;
  step: string; // Title
  updated_at: string;
  content?: string; // Lazy-loaded content
};

// --- Toast Component ---
const Toast = ({ message, type, onClose }: { message: string, type: 'success' | 'error', onClose: () => void }) => (
  <div className={`fixed top-6 right-6 z-50 flex items-center gap-3 px-4 py-3 rounded-lg shadow-xl text-white transform transition-all animate-in slide-in-from-right-10 duration-300 ${type === 'success' ? 'bg-green-600' : 'bg-red-600'}`}>
    {type === 'success' ? <Check size={18} /> : <X size={18} />}
    <span className="font-medium text-sm">{message}</span>
    <button onClick={onClose} className="ml-2 hover:bg-white/20 p-1 rounded"><X size={14}/></button>
  </div>
);

export default function SettingsPage() {
  // 🛡️ SECURITY: Auth Check
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  // --- Main Data State ---
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  
  // --- Filter State ---
  const [filterCategory, setFilterCategory] = useState("All");
  
  // --- System Prompt Editor State ---
  const [name, setName] = useState("");
  const [content, setContent] = useState("");
  const [category, setCategory] = useState("lesson_plan");
  
  const contentRef = useRef(content);
  useEffect(() => {
    contentRef.current = content;
  }, [content]);
  
  // --- Inline Prompts State ---
  const [inlinePrompts, setInlinePrompts] = useState<InlinePrompt[]>([]);
  const [expandedInlineId, setExpandedInlineId] = useState<number | null>(null);
  
  // --- UI/Loading States ---
  const [isEditingCategory, setIsEditingCategory] = useState(false);
  const categoryInputRef = useRef<HTMLInputElement>(null);
  
  const [loading, setLoading] = useState(false);        // System Prompt Saving
  const [contentLoading, setContentLoading] = useState(false); // System Prompt Fetching
  
  // Inline Loading States
  const [inlineListLoading, setInlineListLoading] = useState(false); 
  const [inlineDetailLoadingId, setInlineDetailLoadingId] = useState<number | null>(null); 
  const [inlineSavingId, setInlineSavingId] = useState<number | null>(null); 
  const [creatingInline, setCreatingInline] = useState(false); // 👈 New: Creating state
  
  const [toast, setToast] = useState<{ msg: string, type: 'success' | 'error' } | null>(null);
  
  const [focusedTextarea, setFocusedTextarea] = useState<'system' | number | null>(null);

  const [showExportDialog, setShowExportDialog] = useState(false);
  const [selectedExportIds, setSelectedExportIds] = useState<number[]>([]);
  const [exporting, setExporting] = useState(false);

  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploadFiles, setUploadFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadResults, setUploadResults] = useState<BatchUploadResult[] | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [showOptimizeDialog, setShowOptimizeDialog] = useState(false);
  const [optimizePrompt, setOptimizePrompt] = useState('');
  const [optimizeFiles, setOptimizeFiles] = useState<File[]>([]);
  const [optimizeFileUrls, setOptimizeFileUrls] = useState<string[]>([]);
  const [optimizeUploadingFiles, setOptimizeUploadingFiles] = useState(false);
  const [optimizeRunning, setOptimizeRunning] = useState(false);
  const [optimizeProgress, setOptimizeProgress] = useState<(CurriculumPipelineProgressMessage | SlidesPipelineProgressMessage)[]>([]);
  const optimizeFileInputRef = useRef<HTMLInputElement>(null);
  const pipelineRef = useRef<ReturnType<typeof runCurriculumPipeline> | ReturnType<typeof runSlidesPipeline> | null>(null);

  const [startPhaseIndex, setStartPhaseIndex] = useState(0);
  const [startLoopX, setStartLoopX] = useState(3);
  const [startStepIndex, setStartStepIndex] = useState(0);

  // Security Gate
  useEffect(() => {
    if (!authLoading && user) {
      if (user.role !== 'sys') {
        // Option: Redirect or show Access Denied
      }
    }
  }, [user, authLoading, navigate]);

  const showNotification = (msg: string, type: 'success' | 'error') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        e.stopPropagation();
        
        if (focusedTextarea === 'system') {
          handleSaveSystemPrompt();
        } else if (typeof focusedTextarea === 'number') {
          const target = inlinePrompts.find(p => p.id === focusedTextarea);
          if (target) {
            setInlineSavingId(focusedTextarea);
            updateInlinePrompt(focusedTextarea, { 
              step: target.step, 
              content: target.content 
            })
              .then(() => showNotification("Inline Prompt Saved!", "success"))
              .catch(() => showNotification("Failed to save inline prompt", "error"))
              .finally(() => setInlineSavingId(null));
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [focusedTextarea, inlinePrompts]);

  // Initial Load
  useEffect(() => {
    if (user?.role === 'sys') {
      fetchList();
    }
  }, [user]);

  // --- Fetch Logic (Merged) ---
  useEffect(() => {
    let isActive = true;

    const fetchDetailAndInline = async (id: number) => {
      console.log('🔄 fetchDetailAndInline called for id:', id, 'current selectedId:', selectedId);
      setContentLoading(true);
      setInlineListLoading(true);
      setInlinePrompts([]); 
      setExpandedInlineId(null);

      try {
        const placeholder = prompts.find(p => p.id === id);
        if (placeholder) {
          setName(placeholder.name);
          setCategory(placeholder.category);
          console.log('🔄 Setting content to empty (placeholder)');
          setContent(""); 
        }

        const [sysRes, inlineRes] = await Promise.all([
          getSysPrompt(id),
          getInlinePrompts(id)
        ]);
        
        if (isActive) {
          const detail = sysRes.data;
          setName(detail.name);
          setCategory(detail.category);
          console.log('🔄 Setting content from server, length:', detail.prompt_content?.length || 0);
          setContent(detail.prompt_content || "");
          setInlinePrompts(inlineRes.data);
        }
      } catch (e) {
        if (isActive) {
          console.error(e);
          showNotification("Failed to load details", "error");
        }
      } finally {
        if (isActive) {
          setContentLoading(false);
          setInlineListLoading(false);
        }
      }
    };

    if (selectedId && user?.role === 'sys') {
      fetchDetailAndInline(selectedId);
      setIsEditingCategory(false);
    } else {
      setName(""); 
      setContent("");
      setInlinePrompts([]);
    }

    return () => { isActive = false; };
  }, [selectedId]);

  // --- Actions ---

  const fetchList = async () => {
    try {
      const res = await getSysPrompts();
      setPrompts(res.data);
      if (!selectedId && res.data.length > 0) {
        setSelectedId(res.data[0].id);
      }
    } catch (e) { 
      showNotification("Failed to load prompts list", "error");
    }
  };

  const handleCreate = async () => {
    const newName = prompt("Enter name for new system prompt:");
    if (!newName) return;
    try {
      const res = await createSysPrompt(newName, 'lesson_plan');
      await fetchList();
      setSelectedId(res.data.id);
      showNotification("New prompt created!", "success");
    } catch (e) { 
      showNotification("Failed to create prompt", "error");
    }
  };

  const handleSaveSystemPrompt = async () => {
    if (!selectedId) return;
    setLoading(true);
    try {
      const currentContent = contentRef.current;
      console.log('💾 Current content state length:', currentContent?.length);
      console.log('💾 Current content state preview:', currentContent?.substring(0, 100));
      const payload = { name, category, prompt_content: currentContent };
      console.log('💾 Saving System Prompt:', { selectedId, payload: { ...payload, prompt_content: `[${payload.prompt_content?.length} chars]` } });
      const response = await updateSysPrompt(selectedId, payload);
      console.log('💾 Save response:', response.data);
      setPrompts(prev => prev.map(p => p.id === selectedId ? { ...p, name, category } : p));
      showNotification("System Prompt Saved!", "success");
    } catch (e) {
      console.error('💾 Save failed:', e);
      showNotification("Failed to save system prompt", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedId || !confirm("Delete this prompt?")) return;
    try {
      await deleteSysPrompt(selectedId);
      setSelectedId(null);
      await fetchList();
      showNotification("Prompt deleted", "success");
    } catch (e) { 
      showNotification("Delete failed", "error");
    }
  };

  // --- Inline Prompt Logic ---

  // ➕ Create Inline Prompt
  const handleCreateInline = async () => {
    if (!selectedId) return;
    const stepName = prompt("Enter Step Name for new Inline Prompt:");
    if (!stepName) return;

    setCreatingInline(true);
    try {
      // 默认创建一个内容为空的 inline prompt
      const res = await createInlinePrompt(selectedId, { step: stepName, content: "" });
      
      // 更新本地状态：追加到末尾
      setInlinePrompts(prev => [...prev, res.data]);
      // 自动展开新建的项
      setExpandedInlineId(res.data.id);
      
      showNotification("Inline Prompt Created", "success");
    } catch (e) {
      showNotification("Failed to create inline prompt", "error");
    } finally {
      setCreatingInline(false);
    }
  };

  // 🗑️ Delete Inline Prompt (Soft Delete)
  const handleDeleteInline = async (id: number, e: React.MouseEvent) => {
    e.stopPropagation(); // 防止冒泡导致折叠/展开
    if (!confirm("Are you sure you want to delete this inline prompt?")) return;

    try {
      await deleteInlinePrompt(id);
      // 更新本地状态：移除该项
      setInlinePrompts(prev => prev.filter(p => p.id !== id));
      if (expandedInlineId === id) setExpandedInlineId(null);
      showNotification("Inline Prompt Deleted", "success");
    } catch (e) {
      showNotification("Failed to delete inline prompt", "error");
    }
  };

  const toggleInlineExpand = async (inlineId: number) => {
    if (expandedInlineId === inlineId) {
      setExpandedInlineId(null);
      return;
    }
    setExpandedInlineId(inlineId);
    
    const existing = inlinePrompts.find(p => p.id === inlineId);
    if (existing && existing.content !== undefined) {
      return; 
    }

    setInlineDetailLoadingId(inlineId);
    try {
      const res = await getInlinePromptDetail(inlineId);
      setInlinePrompts(prev => prev.map(p => 
        p.id === inlineId ? { ...p, content: res.data.content } : p
      ));
    } catch (e) {
      showNotification("Failed to load inline content", "error");
    } finally {
      setInlineDetailLoadingId(null);
    }
  };

  const handleInlineChange = (id: number, field: 'step' | 'content', value: string) => {
    setInlinePrompts(prev => prev.map(p => 
      p.id === id ? { ...p, [field]: value } : p
    ));
  };

  const handleSaveInlinePrompt = async (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    const target = inlinePrompts.find(p => p.id === id);
    if (!target) return;

    setInlineSavingId(id);
    try {
      await updateInlinePrompt(id, { 
        step: target.step, 
        content: target.content 
      });
      showNotification("Inline Prompt Saved!", "success");
    } catch (e) {
      showNotification("Failed to save inline prompt", "error");
    } finally {
      setInlineSavingId(null);
    }
  };

  const openExportDialog = () => {
    setSelectedExportIds([]);
    setShowExportDialog(true);
  };

  const toggleExportSelection = (id: number) => {
    setSelectedExportIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const selectAllForExport = () => {
    setSelectedExportIds(inlinePrompts.map(p => p.id));
  };

  const deselectAllForExport = () => {
    setSelectedExportIds([]);
  };

  const sanitizeFilename = (name: string): string => {
    return name.replace(/[<>:"/\\|?*]/g, '_').trim() || 'untitled';
  };

  const handleExport = async () => {
    if (selectedExportIds.length === 0) {
      showNotification("Please select at least one prompt to export", "error");
      return;
    }

    setExporting(true);

    try {
      const promptsToExport = inlinePrompts.filter(p => selectedExportIds.includes(p.id));
      
      const promptsNeedContent = promptsToExport.filter(p => p.content === undefined);
      
      if (promptsNeedContent.length > 0) {
        const detailPromises = promptsNeedContent.map(p => getInlinePromptDetail(p.id));
        const detailResults = await Promise.all(detailPromises);
        
        detailResults.forEach((res, index) => {
          const prompt = promptsNeedContent[index];
          prompt.content = res.data.content;
        });
      }

      const hasFileSystemAccess = 'showDirectoryPicker' in window;
      
      if (hasFileSystemAccess) {
        try {
          const dirHandle = await (window as any).showDirectoryPicker({ mode: 'readwrite' });
          
          for (const prompt of promptsToExport) {
            const fileName = `${sanitizeFilename(prompt.step)}.txt`;
            const fileHandle = await dirHandle.getFileHandle(fileName, { create: true });
            const writable = await fileHandle.createWritable();
            await writable.write(prompt.content || '');
            await writable.close();
          }
          
          showNotification(`Successfully exported ${promptsToExport.length} prompt(s)`, "success");
          setShowExportDialog(false);
        } catch (err: any) {
          if (err.name === 'AbortError') {
            // User cancelled
          } else {
            throw err;
          }
        }
      } else {
        for (const prompt of promptsToExport) {
          const fileName = `${sanitizeFilename(prompt.step)}.txt`;
          const blob = new Blob([prompt.content || ''], { type: 'text/plain' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = fileName;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
          
          await new Promise(resolve => setTimeout(resolve, 100));
        }
        
        showNotification(`Downloaded ${promptsToExport.length} prompt(s) to your default download folder`, "success");
        setShowExportDialog(false);
      }
    } catch (e) {
      console.error('Export error:', e);
      showNotification("Failed to export prompts", "error");
    } finally {
      setExporting(false);
    }
  };

  const openUploadDialog = () => {
    setUploadFiles([]);
    setUploadResults(null);
    setShowUploadDialog(true);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const validFiles = files.filter(file => {
      const ext = file.name.toLowerCase().split('.').pop();
      return ext === 'txt' || ext === 'md';
    });
    setUploadFiles(validFiles);
    setUploadResults(null);
    if (e.target) {
      e.target.value = '';
    }
  };

  const removeUploadFile = (index: number) => {
    setUploadFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (!selectedId || uploadFiles.length === 0) return;

    setUploading(true);
    setUploadResults(null);

    try {
      const res = await batchUploadInlinePrompts(selectedId, uploadFiles);
      setUploadResults(res.data.results);
      
      const successResults = res.data.results.filter(r => r.status === 'success');
      if (successResults.length > 0) {
        const inlineRes = await getInlinePrompts(selectedId);
        
        const fileContentMap = new Map<string, string>();
        for (const file of uploadFiles) {
          const stepName = file.name.replace(/\.(txt|md)$/i, '');
          const content = await file.text();
          fileContentMap.set(stepName, content);
        }
        
        const updatedPrompts = inlineRes.data.map(p => {
          const content = fileContentMap.get(p.step);
          if (content !== undefined) {
            return { ...p, content };
          }
          return p;
        });
        
        setInlinePrompts(updatedPrompts);
      }
      
      if (successResults.length === res.data.results.length) {
        showNotification(`Successfully uploaded ${successResults.length} prompt(s)`, "success");
      } else if (successResults.length > 0) {
        showNotification(`Uploaded ${successResults.length} of ${res.data.results.length} prompt(s)`, "success");
      }
    } catch (e) {
      console.error('Upload error:', e);
      showNotification("Failed to upload prompts", "error");
    } finally {
      setUploading(false);
    }
  };

  const closeUploadDialog = () => {
    setShowUploadDialog(false);
    setUploadFiles([]);
    setUploadResults(null);
  };

  const handleExportSystemPrompt = async () => {
    if (!content) return;

    const fileName = 'sys_prompt.txt';
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    showNotification("System prompt exported as sys_prompt.txt", "success");
  };

  const openOptimizeDialog = () => {
    setOptimizePrompt('');
    setOptimizeFiles([]);
    setOptimizeFileUrls([]);
    setOptimizeProgress([]);
    setStartPhaseIndex(0);
    setStartLoopX(3);
    setStartStepIndex(0);
    setShowOptimizeDialog(true);
  };

  const closeOptimizeDialog = () => {
    if (pipelineRef.current) {
      pipelineRef.current.close();
      pipelineRef.current = null;
    }
    setShowOptimizeDialog(false);
    setOptimizePrompt('');
    setOptimizeFiles([]);
    setOptimizeFileUrls([]);
    setOptimizeProgress([]);
    setOptimizeRunning(false);
  };

  const handleOptimizeFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setOptimizeFiles(prev => [...prev, ...files]);
    if (e.target) {
      e.target.value = '';
    }
  };

  const removeOptimizeFile = (index: number) => {
    setOptimizeFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleRunOptimize = async () => {
    if (!optimizePrompt.trim()) {
      showNotification("Please enter a prompt", "error");
      return;
    }

    setOptimizeUploadingFiles(true);
    const uploadedUrls: string[] = [];

    try {
      for (const file of optimizeFiles) {
        try {
          const res = await uploadToOSS(file);
          if (res.data.file_url) {
            uploadedUrls.push(res.data.file_url);
          }
        } catch (e) {
          console.error('Failed to upload file:', file.name, e);
          showNotification(`Failed to upload ${file.name}`, "error");
          setOptimizeUploadingFiles(false);
          return;
        }
      }

      setOptimizeFileUrls(uploadedUrls);
      setOptimizeUploadingFiles(false);
      setOptimizeRunning(true);
      setOptimizeProgress([]);

      const onMessage = (message: CurriculumPipelineProgressMessage | SlidesPipelineProgressMessage) => {
        setOptimizeProgress(prev => [...prev, message]);
        
        if (message.type === 'completed') {
          setOptimizeRunning(false);
          showNotification("Pipeline completed successfully!", "success");
          if (selectedId) {
            fetchList();
            const fetchDetail = async () => {
              const [sysRes, inlineRes] = await Promise.all([
                getSysPrompt(selectedId),
                getInlinePrompts(selectedId)
              ]);
              setName(sysRes.data.name);
              setCategory(sysRes.data.category);
              setContent(sysRes.data.prompt_content || "");
              setInlinePrompts(inlineRes.data);
            };
            fetchDetail();
          }
        } else if (message.type === 'error') {
          setOptimizeRunning(false);
          showNotification(message.message || "Pipeline error", "error");
        }
      };

      if (category === 'gen_plan') {
        const pipeline = runCurriculumPipeline(
          onMessage,
          () => {
            const currentPhase = PIPELINE_OPTIONS.start_phase_index_options[startPhaseIndex];
            const loopX = currentPhase?.is_loop ? startLoopX : undefined;
            pipeline.start(
              optimizePrompt, 
              uploadedUrls.length > 0 ? uploadedUrls : undefined,
              startPhaseIndex,
              loopX,
              startStepIndex
            );
          }
        );
        pipelineRef.current = pipeline;
      } else if (category === 'gen_slide') {
        const pipeline = runSlidesPipeline(
          onMessage,
          () => {
            pipeline.start(
              optimizePrompt, 
              uploadedUrls.length > 0 ? uploadedUrls : undefined,
              startPhaseIndex,
              startStepIndex
            );
          }
        );
        pipelineRef.current = pipeline;
      }
    } catch (e) {
      console.error('Pipeline error:', e);
      showNotification("Failed to start pipeline", "error");
      setOptimizeUploadingFiles(false);
      setOptimizeRunning(false);
    }
  };

  // --- Computed ---
  const uniqueCategoriesWithAll = useMemo(() => {
    const cats = new Set(prompts.map(p => p.category));
    return ['All', ...Array.from(cats).sort()];
  }, [prompts]);

  const availableCategories = useMemo(() => {
    const cats = new Set(prompts.map(p => p.category));
    ['lesson_plan', 'courseware', 'audit'].forEach(c => cats.add(c));
    return Array.from(cats).sort();
  }, [prompts]);

  const filteredPrompts = useMemo(() => {
    if (filterCategory === 'All') return prompts;
    return prompts.filter(p => p.category === filterCategory);
  }, [prompts, filterCategory]);

  // --- Render ---

  if (authLoading) return <div className="h-screen flex items-center justify-center bg-gray-50"><Loader2 className="animate-spin text-gray-400"/></div>;
  if (!user || user.role !== 'sys') return <div className="h-screen flex flex-col items-center justify-center bg-gray-50 text-red-600 gap-2"><ShieldAlert size={48}/><span className="font-bold">Access Denied: System Administrators Only</span></div>;

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden relative">
      {toast && <Toast message={toast.msg} type={toast.type} onClose={() => setToast(null)} />}

      {/* 1. LEFT SIDEBAR (List) */}
      <div className="w-64 bg-white border-r flex flex-col shrink-0 z-20 shadow-sm">
        <div className="p-3 border-b bg-white">
            <Link to="/admin" className="flex items-center gap-2 text-gray-500 hover:text-gray-900 hover:bg-gray-100 px-3 py-2 rounded-lg transition-colors text-sm font-medium">
                <ArrowLeft size={16} /> Back
            </Link>
        </div>
        <div className="p-4 border-b flex flex-col gap-3 bg-gray-50">
          <div className="flex items-center justify-between">
            <h2 className="font-bold text-gray-800 flex items-center gap-2 text-sm">
              <Lock size={14} className="text-brand-600"/> System Prompts
            </h2>
            <button onClick={handleCreate} className="p-1 hover:bg-white rounded text-brand-600 border border-transparent hover:border-gray-200">
              <Plus size={16}/>
            </button>
          </div>
          <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)} className="w-full text-xs border-gray-200 rounded">
             {uniqueCategoriesWithAll.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="flex-1 overflow-y-auto">
          {filteredPrompts.map(p => (
            <button key={p.id} onClick={() => setSelectedId(p.id)} className={`w-full text-left p-3 text-xs border-b flex justify-between items-center ${selectedId === p.id ? 'bg-brand-50 border-l-4 border-l-brand-600 text-brand-800' : 'text-gray-600 hover:bg-gray-50'}`}>
              <div className="truncate font-medium">{p.name}</div>
            </button>
          ))}
        </div>
      </div>

      {/* 2. MAIN AREA */}
      <div className="flex-1 flex flex-col min-w-0 bg-gray-50">
        {selectedId ? (
          <>
            {/* Header Toolbar */}
            <header className="h-14 bg-white border-b px-6 flex items-center justify-between shrink-0 shadow-sm z-10">
              <div className="flex items-center gap-4 flex-1">
                <input value={name} onChange={e => setName(e.target.value)} className="font-bold text-gray-800 border-none focus:ring-0 p-0 w-64 text-sm" placeholder="Prompt Name..."/>
                <div className="h-4 w-px bg-gray-300"/>
                {isEditingCategory ? (
                   <div className="flex items-center gap-1 animate-in fade-in zoom-in-95 duration-200">
                     <input ref={categoryInputRef} value={category} onChange={e => setCategory(e.target.value)} onBlur={() => setIsEditingCategory(false)} onKeyDown={e => e.key === 'Enter' && setIsEditingCategory(false)} autoFocus className="text-xs bg-white border border-brand-300 rounded-md py-1 px-2 text-gray-800 focus:outline-none focus:ring-2 focus:ring-brand-200 w-32"/>
                     <button onClick={() => setIsEditingCategory(false)} className="text-brand-600 hover:bg-brand-50 p-1 rounded"><Check size={14}/></button>
                   </div>
                ) : (
                   <select value={category} onChange={e => e.target.value === '__NEW__' ? setIsEditingCategory(true) : setCategory(e.target.value)} className="text-xs bg-gray-100 border-none rounded py-1 cursor-pointer">
                     {availableCategories.map(c => <option key={c} value={c}>{c}</option>)}
                     <option value="__NEW__">+ New...</option>
                   </select>
                )}
              </div>
              <div className="flex items-center gap-2">
                 <button onClick={handleDelete} className="p-2 text-gray-400 hover:text-red-600"><Trash2 size={16} /></button>
              </div>
            </header>

            {/* Split View Content */}
            <div className="flex-1 flex flex-row overflow-hidden">
              
              {/* === LEFT: System Prompt Master === */}
              <div className="w-1/2 flex flex-col border-r bg-white">
                <div className="h-10 bg-gray-50 border-b flex items-center justify-between px-4">
                  <span className="text-xs font-bold text-gray-500 flex items-center gap-2">
                    <FileText size={14}/> SYSTEM PROMPT
                  </span>
                  <div className="flex items-center gap-2">
                    {(category === 'gen_plan' || category === 'gen_slide') && (
                      <button 
                        onClick={openOptimizeDialog}
                        className="text-xs flex items-center gap-1 bg-purple-600 text-white px-3 py-1 rounded hover:bg-purple-700 transition-colors"
                        title="Run Optimization Pipeline"
                      >
                        <Sparkles size={12}/> Optimize
                      </button>
                    )}
                    <button 
                      onClick={handleExportSystemPrompt}
                      disabled={!content || contentLoading}
                      className="p-1 hover:bg-white rounded text-gray-600 border border-transparent hover:border-gray-200 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      title="Export System Prompt"
                    >
                      <Download size={14}/>
                    </button>
                    <button 
                      onClick={handleSaveSystemPrompt} 
                      disabled={loading || contentLoading}
                      className="text-xs flex items-center gap-1 bg-gray-900 text-white px-3 py-1 rounded hover:bg-gray-800 disabled:opacity-50 transition-colors"
                    >
                      {loading ? <Loader2 size={12} className="animate-spin"/> : <Save size={12}/>} Save Master
                    </button>
                  </div>
                </div>
                <div className="flex-1 relative">
                  {contentLoading && (
                    <div className="absolute inset-0 bg-white/80 z-10 flex items-center justify-center">
                      <Loader2 className="animate-spin text-brand-600"/>
                    </div>
                  )}
                  <textarea 
                    value={content}
                    onChange={e => {
                      console.log('📝 Textarea onChange, new value length:', e.target.value.length);
                      setContent(e.target.value);
                    }}
                    onFocus={() => setFocusedTextarea('system')}
                    onBlur={() => setFocusedTextarea(null)}
                    className="w-full h-full p-6 outline-none resize-none font-mono text-xs leading-relaxed text-gray-800"
                    placeholder="System prompt content..."
                    spellCheck={false}
                  />
                </div>
              </div>

              {/* === RIGHT: Inline Prompts === */}
              <div className="w-1/2 flex flex-col bg-gray-50">
                <div className="h-10 bg-gray-100 border-b flex items-center justify-between px-4">
                  <span className="text-xs font-bold text-gray-600 flex items-center gap-2">
                    <Layers size={14}/> INLINE PROMPTS
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-400 bg-white px-2 py-0.5 rounded border">
                      {inlinePrompts.length} items
                    </span>
                    <button 
                      onClick={openUploadDialog}
                      className="p-1 hover:bg-white rounded text-gray-600 border border-transparent hover:border-gray-200 transition-colors"
                      title="Upload Inline Prompts"
                    >
                      <Upload size={16}/>
                    </button>
                    <button 
                      onClick={openExportDialog}
                      disabled={inlinePrompts.length === 0}
                      className="p-1 hover:bg-white rounded text-gray-600 border border-transparent hover:border-gray-200 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      title="Export Inline Prompts"
                    >
                      <Download size={16}/>
                    </button>
                    <button 
                      onClick={handleCreateInline}
                      disabled={creatingInline}
                      className="p-1 hover:bg-white rounded text-brand-600 border border-transparent hover:border-gray-200 transition-colors"
                      title="Add Inline Prompt"
                    >
                      {creatingInline ? <Loader2 size={16} className="animate-spin"/> : <Plus size={16}/>}
                    </button>
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                  {inlineListLoading ? (
                    <div className="flex justify-center py-10"><Loader2 className="animate-spin text-gray-400"/></div>
                  ) : inlinePrompts.length === 0 ? (
                    <div className="text-center text-gray-400 text-xs mt-10">No inline prompts found. Click + to add.</div>
                  ) : (
                    inlinePrompts.map((inline) => {
                      const isExpanded = expandedInlineId === inline.id;
                      const isLoadingDetail = inlineDetailLoadingId === inline.id;
                      const isSaving = inlineSavingId === inline.id;

                      return (
                        <div key={inline.id} className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden transition-all">
                          {/* Header */}
                          <div 
                            onClick={() => toggleInlineExpand(inline.id)}
                            className={`flex items-center justify-between p-3 cursor-pointer select-none hover:bg-gray-50 ${isExpanded ? 'bg-gray-50 border-b' : ''}`}
                          >
                            <div className="flex items-center gap-3">
                              <span className={`transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}>
                                <ChevronDown size={14} className="text-gray-400"/>
                              </span>
                              <span className="text-sm font-medium text-gray-700">{inline.step}</span>
                            </div>
                            <span className="text-[10px] text-gray-400 font-mono">ID: {inline.id}</span>
                          </div>

                          {/* Body */}
                          {isExpanded && (
                            <div className="animate-in slide-in-from-top-2 duration-200 border-t border-gray-100">
                              {isLoadingDetail ? (
                                <div className="p-6 flex justify-center">
                                  <Loader2 size={20} className="animate-spin text-brand-400"/>
                                </div>
                              ) : (
                                <div className="flex flex-col bg-white">
                                  
                                  {/* Title Editor */}
                                  <div className="px-3 pt-3 pb-1">
                                    <label className="text-[10px] uppercase font-bold text-gray-400 tracking-wider mb-1 block">
                                      Title / Step Name
                                    </label>
                                    <input 
                                      type="text"
                                      value={inline.step}
                                      onChange={(e) => handleInlineChange(inline.id, 'step', e.target.value)}
                                      className="w-full text-sm font-medium text-gray-800 border border-gray-200 rounded px-2 py-1.5 focus:border-brand-400 focus:ring-1 focus:ring-brand-100 outline-none transition-all"
                                      placeholder="e.g. Generate Outline"
                                    />
                                  </div>

                                  {/* Content Editor */}
                                  <div className="px-3 pb-2">
                                    <label className="text-[10px] uppercase font-bold text-gray-400 tracking-wider mb-1 block mt-2">
                                      Prompt Content
                                    </label>
                                    <textarea
                                      value={inline.content || ""}
                                      onChange={(e) => handleInlineChange(inline.id, 'content', e.target.value)}
                                      onFocus={() => setFocusedTextarea(inline.id)}
                                      onBlur={() => setFocusedTextarea(null)}
                                      className="w-full h-40 p-3 text-xs font-mono text-gray-700 bg-gray-50 border border-gray-200 rounded resize-y outline-none focus:bg-white focus:border-brand-400 focus:ring-1 focus:ring-brand-100 transition-all"
                                      placeholder="Enter inline prompt content..."
                                    />
                                  </div>

                                  {/* Footer Actions */}
                                  <div className="bg-gray-50 px-3 py-2 flex justify-between items-center border-t border-gray-100">
                                    {/* 🆕 Delete Button (Left) */}
                                    <button
                                      onClick={(e) => handleDeleteInline(inline.id, e)}
                                      className="text-gray-400 hover:text-red-600 p-1.5 rounded hover:bg-red-50 transition-colors"
                                      title="Delete Inline Prompt"
                                    >
                                      <Trash2 size={14}/>
                                    </button>

                                    <div className="flex items-center gap-3">
                                        <span className="text-[10px] text-gray-400">
                                        Updated: {new Date(inline.updated_at).toLocaleDateString()}
                                        </span>
                                        <button
                                        onClick={(e) => handleSaveInlinePrompt(inline.id, e)}
                                        disabled={isSaving}
                                        className="flex items-center gap-1.5 bg-gray-900 hover:bg-gray-800 text-white px-4 py-1.5 rounded text-xs font-medium shadow-sm transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                        {isSaving ? <Loader2 size={12} className="animate-spin"/> : <Save size={12}/>}
                                        Save Changes
                                        </button>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

            </div>
          </>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-gray-300">
            <Layout size={64} className="opacity-20 mb-4"/>
            <p className="text-lg font-medium">Select a system prompt</p>
          </div>
        )}
      </div>

      {showExportDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-2xl w-[480px] max-h-[80vh] flex flex-col animate-in zoom-in-95 duration-200">
            <div className="px-5 py-4 border-b flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-brand-50 rounded-lg">
                  <FolderOpen size={20} className="text-brand-600"/>
                </div>
                <div>
                  <h3 className="font-bold text-gray-900">Export Inline Prompts</h3>
                  <p className="text-xs text-gray-500">Select prompts to export as .txt files</p>
                </div>
              </div>
              <button 
                onClick={() => setShowExportDialog(false)}
                className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X size={18} className="text-gray-400"/>
              </button>
            </div>

            <div className="px-5 py-3 border-b bg-gray-50 flex items-center justify-between">
              <span className="text-xs text-gray-500">
                {selectedExportIds.length} of {inlinePrompts.length} selected
              </span>
              <div className="flex gap-2">
                <button 
                  onClick={selectAllForExport}
                  className="text-xs text-brand-600 hover:text-brand-700 font-medium"
                >
                  Select All
                </button>
                <span className="text-gray-300">|</span>
                <button 
                  onClick={deselectAllForExport}
                  className="text-xs text-gray-500 hover:text-gray-700 font-medium"
                >
                  Deselect All
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-3">
              {inlinePrompts.length === 0 ? (
                <div className="text-center text-gray-400 text-sm py-8">
                  No inline prompts available
                </div>
              ) : (
                <div className="space-y-1">
                  {inlinePrompts.map(prompt => (
                    <label 
                      key={prompt.id}
                      className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all ${
                        selectedExportIds.includes(prompt.id) 
                          ? 'bg-brand-50 border border-brand-200' 
                          : 'bg-white border border-gray-100 hover:border-gray-200'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedExportIds.includes(prompt.id)}
                        onChange={() => toggleExportSelection(prompt.id)}
                        className="w-4 h-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm text-gray-800 truncate">{prompt.step}</div>
                        <div className="text-xs text-gray-400">ID: {prompt.id}</div>
                      </div>
                      <FileText size={16} className="text-gray-300"/>
                    </label>
                  ))}
                </div>
              )}
            </div>

            <div className="px-5 py-4 border-t bg-gray-50 rounded-b-xl flex items-center justify-between">
              <div className="text-xs text-gray-400">
                {'showDirectoryPicker' in window ? (
                  <span className="flex items-center gap-1">
                    <FolderOpen size={12}/> Choose save folder
                  </span>
                ) : (
                  <span>Files will download to default folder</span>
                )}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowExportDialog(false)}
                  className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleExport}
                  disabled={selectedExportIds.length === 0 || exporting}
                  className="flex items-center gap-2 px-4 py-2 text-sm bg-brand-600 text-white rounded-lg hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {exporting ? (
                    <>
                      <Loader2 size={14} className="animate-spin"/>
                      Exporting...
                    </>
                  ) : (
                    <>
                      <Download size={14}/>
                      Export ({selectedExportIds.length})
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showUploadDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-2xl w-[520px] max-h-[80vh] flex flex-col animate-in zoom-in-95 duration-200">
            <div className="px-5 py-4 border-b flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-50 rounded-lg">
                  <Upload size={20} className="text-green-600"/>
                </div>
                <div>
                  <h3 className="font-bold text-gray-900">Upload Inline Prompts</h3>
                  <p className="text-xs text-gray-500">Select .txt or .md files to upload</p>
                </div>
              </div>
              <button 
                onClick={closeUploadDialog}
                className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X size={18} className="text-gray-400"/>
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".txt,.md"
                onChange={handleFileSelect}
                className="hidden"
              />
              
              <div 
                onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-gray-200 rounded-lg p-8 text-center cursor-pointer hover:border-brand-300 hover:bg-brand-50/50 transition-all"
              >
                <Upload size={32} className="mx-auto text-gray-300 mb-3"/>
                <p className="text-sm text-gray-600 font-medium">Click to select files</p>
                <p className="text-xs text-gray-400 mt-1">Supports .txt and .md files</p>
              </div>

              {uploadFiles.length > 0 && (
                <div className="mt-4 space-y-2">
                  <div className="text-xs font-medium text-gray-500 mb-2">Selected Files ({uploadFiles.length})</div>
                  {uploadFiles.map((file, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg group">
                      <div className="flex items-center gap-2 min-w-0">
                        <FileText size={14} className="text-gray-400 shrink-0"/>
                        <span className="text-sm text-gray-700 truncate">{file.name}</span>
                        <span className="text-xs text-gray-400">({(file.size / 1024).toFixed(1)} KB)</span>
                      </div>
                      <button
                        onClick={() => removeUploadFile(index)}
                        className="p-1 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all"
                      >
                        <X size={14}/>
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {uploadResults && (
                <div className="mt-4 space-y-2">
                  <div className="text-xs font-medium text-gray-500 mb-2">Upload Results</div>
                  {uploadResults.map((result, index) => (
                    <div 
                      key={index}
                      className={`flex items-center gap-2 p-2 rounded-lg ${
                        result.status === 'success' ? 'bg-green-50' :
                        result.status === 'not_found' ? 'bg-yellow-50' : 'bg-red-50'
                      }`}
                    >
                      {result.status === 'success' ? (
                        <Check size={14} className="text-green-600"/>
                      ) : result.status === 'not_found' ? (
                        <AlertCircle size={14} className="text-yellow-600"/>
                      ) : (
                        <X size={14} className="text-red-600"/>
                      )}
                      <div className="flex-1 min-w-0">
                        <span className="text-sm font-medium text-gray-700">{result.filename}</span>
                        <span className="text-xs text-gray-500 ml-2">→ {result.step}</span>
                      </div>
                      <span className={`text-xs ${
                        result.status === 'success' ? 'text-green-600' :
                        result.status === 'not_found' ? 'text-yellow-600' : 'text-red-600'
                      }`}>
                        {result.message}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="px-5 py-4 border-t bg-gray-50 rounded-b-xl flex items-center justify-end gap-2">
              <button
                onClick={closeUploadDialog}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
              >
                {uploadResults ? 'Close' : 'Cancel'}
              </button>
              {!uploadResults && (
                <button
                  onClick={handleUpload}
                  disabled={uploadFiles.length === 0 || uploading}
                  className="flex items-center gap-2 px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {uploading ? (
                    <>
                      <Loader2 size={14} className="animate-spin"/>
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload size={14}/>
                      Upload ({uploadFiles.length})
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {showOptimizeDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-2xl w-[600px] max-h-[85vh] flex flex-col animate-in zoom-in-95 duration-200">
            <div className="px-5 py-4 border-b flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-50 rounded-lg">
                  <Sparkles size={20} className="text-purple-600"/>
                </div>
                <div>
                  <h3 className="font-bold text-gray-900">Optimize Prompts Pipeline</h3>
                  <p className="text-xs text-gray-500">
                    {category === 'gen_plan' ? 'Run AI-powered optimization for gen_plan prompts' : 'Run AI-powered optimization for gen_slide prompts'}
                  </p>
                </div>
              </div>
              <button 
                onClick={closeOptimizeDialog}
                className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X size={18} className="text-gray-400"/>
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              <input
                ref={optimizeFileInputRef}
                type="file"
                multiple
                onChange={handleOptimizeFileSelect}
                className="hidden"
              />
              
              {category === 'gen_plan' ? (
                <>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="text-xs font-medium text-gray-600 mb-1.5 block">阶段</label>
                      <select
                        value={startPhaseIndex}
                        onChange={(e) => {
                          const newPhaseIndex = parseInt(e.target.value);
                          setStartPhaseIndex(newPhaseIndex);
                          setStartStepIndex(0);
                          const phase = PIPELINE_OPTIONS.start_phase_index_options[newPhaseIndex];
                          if (phase?.is_loop && phase.start_loop_x_options) {
                            setStartLoopX(phase.start_loop_x_options[0].value);
                          }
                        }}
                        className="w-full text-xs border border-gray-200 rounded-lg px-2 py-2 focus:border-purple-400 focus:ring-1 focus:ring-purple-100 outline-none"
                        disabled={optimizeRunning}
                      >
                        {PIPELINE_OPTIONS.start_phase_index_options.map((phase) => (
                          <option key={phase.value} value={phase.value}>{phase.label}</option>
                        ))}
                      </select>
                    </div>

                    {PIPELINE_OPTIONS.start_phase_index_options[startPhaseIndex]?.is_loop && (
                      <div>
                        <label className="text-xs font-medium text-gray-600 mb-1.5 block">环节</label>
                        <select
                          value={startLoopX}
                          onChange={(e) => setStartLoopX(parseInt(e.target.value))}
                          className="w-full text-xs border border-gray-200 rounded-lg px-2 py-2 focus:border-purple-400 focus:ring-1 focus:ring-purple-100 outline-none"
                          disabled={optimizeRunning}
                        >
                          {PIPELINE_OPTIONS.start_phase_index_options[startPhaseIndex].start_loop_x_options?.map((loop) => (
                            <option key={loop.value} value={loop.value}>{loop.label}</option>
                          ))}
                        </select>
                      </div>
                    )}

                    <div>
                      <label className="text-xs font-medium text-gray-600 mb-1.5 block">步骤</label>
                      <select
                        value={startStepIndex}
                        onChange={(e) => setStartStepIndex(parseInt(e.target.value))}
                        className="w-full text-xs border border-gray-200 rounded-lg px-2 py-2 focus:border-purple-400 focus:ring-1 focus:ring-purple-100 outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={optimizeRunning || PIPELINE_OPTIONS.start_phase_index_options[startPhaseIndex]?.start_step_index_options?.length === 0}
                      >
                        {PIPELINE_OPTIONS.start_phase_index_options[startPhaseIndex]?.start_step_index_options?.map((step) => (
                          <option key={step.value} value={step.value}>{step.label}</option>
                        ))}
                        {PIPELINE_OPTIONS.start_phase_index_options[startPhaseIndex]?.start_step_index_options?.length === 0 && (
                          <option value={0}>无步骤</option>
                        )}
                      </select>
                    </div>
                  </div>

                  {PIPELINE_OPTIONS.start_phase_index_options[startPhaseIndex] && (
                    <div className="text-xs text-gray-500 bg-gray-50 p-2 rounded-lg">
                      {PIPELINE_OPTIONS.start_phase_index_options[startPhaseIndex].description}
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-medium text-gray-600 mb-1.5 block">阶段</label>
                      <select
                        value={startPhaseIndex}
                        onChange={(e) => {
                          const newPhaseIndex = parseInt(e.target.value);
                          setStartPhaseIndex(newPhaseIndex);
                          setStartStepIndex(0);
                        }}
                        className="w-full text-xs border border-gray-200 rounded-lg px-2 py-2 focus:border-purple-400 focus:ring-1 focus:ring-purple-100 outline-none"
                        disabled={optimizeRunning}
                      >
                        {SLIDES_PIPELINE_OPTIONS.start_phase_index_options.map((phase) => (
                          <option key={phase.value} value={phase.value}>{phase.label}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="text-xs font-medium text-gray-600 mb-1.5 block">步骤</label>
                      <select
                        value={startStepIndex}
                        onChange={(e) => setStartStepIndex(parseInt(e.target.value))}
                        className="w-full text-xs border border-gray-200 rounded-lg px-2 py-2 focus:border-purple-400 focus:ring-1 focus:ring-purple-100 outline-none"
                        disabled={optimizeRunning}
                      >
                        {SLIDES_PIPELINE_OPTIONS.start_phase_index_options[startPhaseIndex]?.start_step_index_options?.map((step) => (
                          <option key={step.value} value={step.value}>{step.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {SLIDES_PIPELINE_OPTIONS.start_phase_index_options[startPhaseIndex] && (
                    <div className="text-xs text-gray-500 bg-gray-50 p-2 rounded-lg">
                      {SLIDES_PIPELINE_OPTIONS.start_phase_index_options[startPhaseIndex].description}
                    </div>
                  )}
                </>
              )}

              <div>
                <label className="text-xs font-medium text-gray-600 mb-2 block">Prompt</label>
                <textarea
                  value={optimizePrompt}
                  onChange={(e) => setOptimizePrompt(e.target.value)}
                  placeholder="Enter your optimization instructions..."
                  className="w-full h-24 p-3 text-sm border border-gray-200 rounded-lg resize-none focus:border-purple-400 focus:ring-1 focus:ring-purple-100 outline-none"
                  disabled={optimizeRunning}
                />
              </div>

              <div>
                <label className="text-xs font-medium text-gray-600 mb-2 block">Attachments (Optional)</label>
                <div 
                  onClick={() => !optimizeRunning && optimizeFileInputRef.current?.click()}
                  className={`border-2 border-dashed border-gray-200 rounded-lg p-4 text-center cursor-pointer hover:border-purple-300 hover:bg-purple-50/50 transition-all ${optimizeRunning ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <Paperclip size={20} className="mx-auto text-gray-300 mb-2"/>
                  <p className="text-xs text-gray-500">Click to attach files</p>
                </div>
                
                {optimizeFiles.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {optimizeFiles.map((file, index) => (
                      <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg group">
                        <div className="flex items-center gap-2 min-w-0">
                          <Paperclip size={12} className="text-gray-400 shrink-0"/>
                          <span className="text-xs text-gray-700 truncate">{file.name}</span>
                        </div>
                        {!optimizeRunning && (
                          <button
                            onClick={() => removeOptimizeFile(index)}
                            className="p-1 text-gray-400 hover:text-red-500"
                          >
                            <X size={12}/>
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {optimizeProgress.length > 0 && (
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-2 block">Progress</label>
                  <div className="border border-gray-200 rounded-lg max-h-60 overflow-y-auto">
                    {optimizeProgress.map((msg, index) => (
                      <div 
                        key={index}
                        className={`p-2 border-b last:border-b-0 text-xs ${
                          msg.type === 'error' ? 'bg-red-50' :
                          msg.type === 'completed' ? 'bg-green-50' :
                          msg.status === 'success' ? 'bg-green-50/50' :
                          msg.status === 'error' ? 'bg-red-50/50' :
                          'bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          {msg.type === 'start' && <span className="text-blue-600 font-medium">▶ Start</span>}
                          {msg.type === 'progress' && (
                            <>
                              {msg.status === 'processing' && <Loader2 size={12} className="animate-spin text-blue-500"/>}
                              {msg.status === 'success' && <Check size={12} className="text-green-500"/>}
                              {msg.status === 'error' && <X size={12} className="text-red-500"/>}
                              <span className="font-medium text-gray-700">{msg.step}</span>
                            </>
                          )}
                          {msg.type === 'completed' && <span className="text-green-600 font-medium">✓ Completed</span>}
                          {msg.type === 'error' && <span className="text-red-600 font-medium">✗ Error</span>}
                        </div>
                        {msg.message && <div className="text-gray-500 mt-0.5">{msg.message}</div>}
                        {msg.processed_steps !== undefined && msg.total_steps && (
                          <div className="mt-1">
                            <div className="flex justify-between text-gray-400 mb-1">
                              <span>{msg.processed_steps}/{msg.total_steps}</span>
                              <span>{Math.round((msg.processed_steps / msg.total_steps) * 100)}%</span>
                            </div>
                            <div className="h-1 bg-gray-200 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-purple-500 transition-all"
                                style={{ width: `${(msg.processed_steps / msg.total_steps) * 100}%` }}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="px-5 py-4 border-t bg-gray-50 rounded-b-xl flex items-center justify-between">
              <div className="text-xs text-gray-400">
                {optimizeRunning && optimizeProgress.length > 0 && (
                  (() => {
                    const lastProgress = [...optimizeProgress].reverse().find(p => p.processed_steps !== undefined);
                    return lastProgress ? (
                      <span>Step {lastProgress.processed_steps} of {lastProgress.total_steps}</span>
                    ) : null;
                  })()
                )}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={closeOptimizeDialog}
                  className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  {optimizeRunning ? 'Close' : 'Cancel'}
                </button>
                {!optimizeRunning && optimizeProgress.length === 0 && (
                  <button
                    onClick={handleRunOptimize}
                    disabled={!optimizePrompt.trim() || optimizeUploadingFiles}
                    className="flex items-center gap-2 px-4 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {optimizeUploadingFiles ? (
                      <>
                        <Loader2 size={14} className="animate-spin"/>
                        Uploading files...
                      </>
                    ) : (
                      <>
                        <Sparkles size={14}/>
                        Run Pipeline
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
